import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Check, MessageSquare, RefreshCw, Repeat, ThumbsDown } from "lucide-react";
import { toast } from "sonner";
import api, { fmtErr } from "../../lib/api";
import { Card, EmptyState, FileList, ProgressBar, Spinner, StatusChip, TypeChip, fmtMoney } from "../ui";

function Row({ label, value }) {
  if (!value) return null;
  return (
    <div className="flex justify-between gap-6 py-2.5 border-b border-white/5 last:border-0">
      <span className="text-xs tracking-[0.15em] uppercase text-white/40 shrink-0">{label}</span>
      <span className="text-sm text-white/80 text-right">{value}</span>
    </div>
  );
}

function DeliveryProgress({ rid }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get(`/portal/requests/${rid}/milestones`).then((r) => setData(r.data)).catch(() => setData(false));
  }, [rid]);
  if (!data || (data.items.length === 0 && !data.summary?.ordered)) return null;
  return (
    <Card title="Delivery Progress" testId="customer-delivery-card">
      {data.summary && (
        <div className="space-y-4 mb-2" data-testid="customer-delivery-progress">
          {data.summary.locations.map((l) => (
            <div key={l.name}>
              <div className="flex justify-between text-sm mb-1.5">
                <span className="text-white/75">{l.name}</span>
                <span className="text-white/50">{Number(l.delivered).toLocaleString()} / {Number(l.ordered).toLocaleString()} {data.summary.unit}</span>
              </div>
              <ProgressBar delivered={l.delivered} ordered={l.ordered} testId={`customer-progress-${l.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            </div>
          ))}
          <div className="flex justify-between text-sm pt-2 border-t border-white/8">
            <span className="text-white/50">Total delivered</span>
            <span className="text-gold font-outfit" data-testid="customer-delivery-total">
              {Number(data.summary.delivered).toLocaleString()} / {Number(data.summary.ordered).toLocaleString()} {data.summary.unit}
            </span>
          </div>
        </div>
      )}
      {data.items.length > 0 && (
        <div className="mt-5 space-y-3" data-testid="customer-milestone-list">
          {data.items.map((m) => (
            <div key={m.id} className="border border-white/10 rounded-lg p-4">
              <div className="flex justify-between text-sm">
                <span className="text-white/80">{Number(m.volume).toLocaleString()} {data.summary?.unit}{m.location ? ` — ${m.location}` : ""}</span>
                <span className="text-white/40">{m.delivery_date}</span>
              </div>
              {m.notes && <p className="text-xs text-white/45 mt-1">{m.notes}</p>}
              {m.files?.length > 0 && <div className="mt-2"><FileList files={m.files} /></div>}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

export default function RequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [note, setNote] = useState("");
  const [showNote, setShowNote] = useState(null);
  const [acting, setActing] = useState(false);

  const load = () => api.get(`/portal/requests/${id}`).then((r) => setData(r.data)).catch(() => setData(false));
  useEffect(() => { load(); }, [id]);

  if (data === null) return <div className="py-24"><Spinner /></div>;
  if (data === false) return <EmptyState title="Request not found" text="This request does not exist or does not belong to your account." />;

  const r = data.request;
  const p = data.proposal;
  const ref = r.request_ref || r.reference_id;

  const act = async (action) => {
    if (acting) return;
    setActing(true);
    try {
      await api.post(`/portal/requests/${id}/proposal`, { action, note });
      toast.success(action === "approve" ? "Proposal approved" : action === "clarify" ? "Clarification requested" : "Proposal declined");
      setShowNote(null);
      setNote("");
      load();
    } catch (err) {
      toast.error("Action failed", { description: fmtErr(err) });
    } finally {
      setActing(false);
    }
  };

  const repeat = async (mode) => {
    try {
      const res = await api.post(`/portal/requests/${id}/repeat`, { mode });
      toast.success("New request created", { description: res.data.request_ref });
      navigate(`/portal/requests/${res.data.id}`);
    } catch (err) {
      toast.error("Repeat failed", { description: fmtErr(err) });
    }
  };

  return (
    <div data-testid="customer-request-detail" className="max-w-4xl">
      <button onClick={() => navigate("/portal")} className="text-sm text-white/45 hover:text-gold transition-colors mb-6" data-testid="back-to-dashboard">
        <ArrowLeft className="w-4 h-4 inline mr-1" /> Dashboard
      </button>

      <div className="flex flex-wrap items-center gap-4 mb-8">
        <h1 className="font-outfit text-3xl font-extralight tracking-tight text-white" data-testid="request-ref">{ref}</h1>
        <TypeChip type={r.request_type || r.type} />
        <StatusChip status={r.status} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          <Card title="Requirement" testId="requirement-card">
            <Row label="Product" value={r.product} />
            <Row label={r.request_type === "recurring" || r.request_type === "multi_location" ? "Est. monthly volume" : "Quantity"} value={`${r.quantity} ${r.unit || ""}`} />
            <Row label="Delivery location" value={r.delivery_location} />
            <Row label="Required date" value={r.required_date} />
            <Row label="Start date" value={r.start_date} />
            <Row label="Frequency" value={r.frequency} />
            <Row label="Duration" value={r.expected_duration} />
            <Row label="Arrangement" value={r.commercial_arrangement} />
            <Row label="Country" value={r.country} />
            <Row label="Specifications" value={r.specs} />
            <Row label="Existing supplier" value={r.existing_supplier} />
            <Row label="Notes" value={r.notes} />
            {r.locations?.length > 0 && (
              <div className="pt-4">
                <p className="text-xs tracking-[0.15em] uppercase text-white/40 mb-3">Locations</p>
                {r.locations.map((l, i) => (
                  <div key={i} className="flex justify-between text-sm py-2 border-b border-white/5 last:border-0">
                    <span className="text-white/75">{l.name}</span>
                    <span className="text-white/50">{l.volume} {r.unit}</span>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {p && (
            <Card testId="proposal-card" className={p.status === "sent" || p.status === "clarification" ? "border-gold/40" : ""}>
              <div className="flex items-center justify-between mb-6">
                <h3 className="font-outfit text-lg font-light text-white">Ajemi Procurement Proposal</h3>
                <StatusChip status={p.status === "sent" ? "PROPOSAL SENT" : p.status.toUpperCase()} />
              </div>
              <Row label="Product" value={p.product} />
              <Row label="Quantity" value={`${Number(p.quantity).toLocaleString()} ${p.unit}`} />
              <Row label="Price" value={`${fmtMoney(p.price_per_unit, p.currency)} / ${p.unit.replace(/s$/, "")}`} />
              <Row label="Total" value={fmtMoney(p.total_price, p.currency)} />
              <Row label="Delivery terms" value={p.delivery_terms} />
              <Row label="Payment terms" value={p.payment_terms} />
              <Row label="Delivery timeline" value={p.delivery_timeline} />
              <Row label="Quote validity" value={p.validity} />
              <Row label="Notes" value={p.notes} />
              {p.customer_note && <Row label="Your note" value={p.customer_note} />}

              {(p.status === "sent" || p.status === "clarification") && (
                <div className="mt-7">
                  {showNote ? (
                    <div>
                      <textarea rows={3} value={note} onChange={(e) => setNote(e.target.value)} className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-4 py-3 text-sm text-white placeholder-white/30 focus:border-gold/60 focus:outline-none resize-none" placeholder="Add a note for the Ajemi team…" data-testid="proposal-note-input" />
                      <div className="flex gap-3 mt-3">
                        <button onClick={() => act(showNote)} disabled={acting} className="bg-gold text-ink text-sm font-outfit font-medium rounded-full px-6 py-2.5 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="proposal-confirm-note-btn">
                          {showNote === "clarify" ? "Send Clarification Request" : "Confirm Decline"}
                        </button>
                        <button onClick={() => setShowNote(null)} className="text-sm text-white/50 hover:text-white transition-colors" data-testid="proposal-cancel-note-btn">Cancel</button>
                      </div>
                    </div>
                  ) : (
                    <div className="flex flex-wrap gap-3" data-testid="proposal-actions">
                      <button onClick={() => act("approve")} disabled={acting} className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-6 py-2.5 hover:bg-goldbright transition-colors disabled:opacity-60" data-testid="proposal-approve-btn">
                        <Check className="w-4 h-4" /> Approve
                      </button>
                      <button onClick={() => setShowNote("clarify")} className="inline-flex items-center gap-2 border border-white/20 text-white/80 text-sm font-outfit rounded-full px-6 py-2.5 hover:border-gold/50 hover:text-gold transition-colors" data-testid="proposal-clarify-btn">
                        <MessageSquare className="w-4 h-4" /> Request Clarification
                      </button>
                      <button onClick={() => setShowNote("decline")} className="inline-flex items-center gap-2 border border-white/20 text-white/60 text-sm font-outfit rounded-full px-6 py-2.5 hover:border-red-400/50 hover:text-red-300 transition-colors" data-testid="proposal-decline-btn">
                        <ThumbsDown className="w-4 h-4" /> Decline
                      </button>
                    </div>
                  )}
                </div>
              )}
              {p.status === "approved" && (
                <p className="mt-6 text-sm text-emerald-300" data-testid="proposal-approved-note">You approved this proposal. Ajemi is confirming supply and coordinating fulfillment.</p>
              )}
            </Card>
          )}

          <Card title="Documents" testId="documents-card">
            <FileList files={r.files} />
          </Card>

          <DeliveryProgress rid={id} />

          {(r.status === "DELIVERED" || r.status === "COMPLETED") && (
            <Card testId="repeat-card" className="border-gold/30">
              <h3 className="font-outfit text-lg font-light text-white mb-2">Need this again?</h3>
              <p className="text-sm text-white/50 mb-5">The second order should be easier than the first.</p>
              <div className="flex flex-wrap gap-3">
                <button onClick={() => repeat("repeat")} className="inline-flex items-center gap-2 bg-gold text-ink text-sm font-outfit font-medium rounded-full px-6 py-2.5 hover:bg-goldbright transition-colors" data-testid="repeat-procurement-btn">
                  <Repeat className="w-4 h-4" /> Repeat Procurement
                </button>
                <button onClick={() => repeat("recurring")} className="inline-flex items-center gap-2 border border-gold/50 text-gold text-sm font-outfit rounded-full px-6 py-2.5 hover:bg-gold hover:text-ink transition-colors" data-testid="setup-recurring-btn">
                  <RefreshCw className="w-4 h-4" /> Set Up Recurring Supply
                </button>
              </div>
            </Card>
          )}
        </div>

        <div className="lg:col-span-2 space-y-6">
          <Card title="Progress" testId="status-timeline-card">
            <div className="space-y-0">
              {[...(r.status_history || [])].reverse().map((h, i) => (
                <div key={i} className="relative pl-7 pb-6 last:pb-0">
                  {i < (r.status_history?.length || 0) - 1 && <span className="absolute left-[5px] top-4 bottom-0 w-px bg-white/10" />}
                  <span className={`absolute left-0 top-1.5 w-[11px] h-[11px] rounded-full ${i === 0 ? "bg-gold shadow-[0_0_12px_rgba(217,164,65,0.5)]" : "bg-white/20"}`} />
                  <p className={`text-sm ${i === 0 ? "text-gold" : "text-white/60"}`}>{h.status}</p>
                  <p className="text-xs text-white/35 mt-0.5">{new Date(h.at).toLocaleString()} · {h.by}</p>
                </div>
              ))}
            </div>
          </Card>

          {data.cycles?.length > 0 && (
            <Card title="Supply Cycles" testId="cycles-card">
              {data.cycles.map((c) => (
                <div key={c.id} className="flex justify-between items-center py-2.5 border-b border-white/5 last:border-0">
                  <span className="text-sm text-white/75">{c.label}</span>
                  <span className="text-xs text-white/45 capitalize">{c.status}</span>
                </div>
              ))}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
